import PropTypes from 'prop-types';
import React from 'react';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Heading from '../../components/Heading/Heading';
import Content from '../../components/Content/Content';
import { Link } from 'react-router-dom';
import Posts from '../../components/Posts/Posts';
import CTACardv2 from '../../components/CTACardv2/CTACardv2';
import theme from '../../styles/theme';

function ForeclosurePrevention({ isLoading = false }) {
  return (
    <Layout isLoading={isLoading}>
      <SEO
        title='Tips for Preventing a Home Foreclosure'
        description='There are options to preventing a home foreclosure. If you are experiencing financial hardships and are worried about your loan payments, please call Vanderbilt Mortgage and Finance, Inc. today.'
      />

      <Heading appearance='dark' background={theme.colors.blue10}>
        <h1>Foreclosure Prevention</h1>
      </Heading>

      <Content>
        <h2>Options Exist to Help Avoid Foreclosure</h2>

        <p className='color-grey1 no-margin-top'>
          If you are experiencing financial hardships and are worried that you
          cannot make your scheduled loan payments, please call Vanderbilt
          Mortgage and Finance, Inc. today at{' '}
          <a href='tel:18009707250'>1-800-970-7250</a> so that one of our
          experienced account representatives can review the options that are
          available to assist you.
        </p>
        <p className='color-grey1 no-margin-top'>
          Available options vary with the unique circumstances of each
          individual, but some of the different alternatives that exist include:
        </p>
        <ul>
          <li>Repayment Plans</li>
          <li>Extensions from 1-3 months</li>
          <li>
            Debt management programs with independent, non-profit agencies
          </li>
          <li>Modifications</li>
          <li>
            Creating or increasing a balloon payment due at the maturity of your
            loan
          </li>
          <li>
            <Link to='/resources/faqs/your-home/how-do-i-sell-my-home/'>
              Sell Your Home
            </Link>
          </li>
        </ul>
      </Content>

      <Content>
        <h2>Documents Needed to Discuss these Options:</h2>

        <p className='color-grey1 no-margin-top'>
          To help determine if any of the available options will best be able to
          assist you personally, you will want to have various documents
          available during your discussion with Vanderbilt Mortgage. These
          documents include tax returns, recent pay stubs, bank statements,
          monthly bills, and if relevant, medical and other unanticipated bills.
        </p>
      </Content>

      <Content>
        <h2>HUD-Certified Counseling Agencies:</h2>

        <p className='color-grey1 no-margin-top'>
          HUD is a U.S. government agency that offers free or low-cost
          counseling to homeowners in the US. Please call{' '}
          <a href='tel:18005694287'>1-800-569-4287</a> for a list of the HUD
          agencies in your area or visit their website at{' '}
          <a
            href='https://www.hud.gov'
            target='_blank'
            rel='noreferrer noopener'
          >
            www.HUD.gov
          </a>
          .
        </p>
      </Content>

      <Content>
        <h2>Equal Credit Opportunity Act (ECOA):</h2>

        <p className='color-grey1 no-margin-top'>
          A federal law that prohibits lenders from unlawfully discriminating
          against consumers when applying for credit. Learn more about&nbsp;
          <a
            href='https://www.consumerfinance.gov/about-us/blog/what-you-need-know-about-equal-credit-opportunity-act-and-how-it-can-help-you-why-it-was-passed-and-what-it/'
            target='_blank'
            rel='noreferrer noopener'
          >
            ECOA
          </a>
          &nbsp;from the Consumer Financial Protection Bureau’s blog and how it
          protects borrowers.
        </p>
      </Content>

      <Posts
        title='Helpful Resources from the Consumer Financial Protection Bureau'
        posts={[
          {
            title:
              'Want Credit to Work for You - Start with These Steps from the Consumer Financial Protection Bureau',
            contentTag: 'Guide',
            category: 'Home Buying',
            thumbnail:
              '//images.ctfassets.net/y64yks2od8ab/7gpLzjyWVh4XagIPXE8zKB/ae46e5ffe58bb443b41fcac3f1062297/Want_Credit_to_Work_for_You_-_Start_with_These_Steps.jpg',
            slug: '/resources/blog/understanding-credit/',
            excerpt:
              'Looking at what you can do to improve your credit? Check out these 8 tools that will help you understand credit histories, reports and scores. ',
            mediaType: 'Image',
          },
          {
            title:
              'Behind on Bills - Start with One Step from the Consumer Financial Protection Bureau',
            contentTag: 'Guide',
            category: 'Budgeting',
            thumbnail:
              '//images.ctfassets.net/y64yks2od8ab/7jXFkoRCZSmoVdelDVkksW/2c7dd306a613959430b970b01ea59f2f/Behind_on_Bills_-_Start_with_One_Step.jpg',
            slug: '/resources/blog/tips-for-managing-bills/',
            excerpt: 'Falling behind on bills? We have 8 tools to help. ',
            mediaType: 'Image',
          },
          {
            title:
              'Debt Getting in Your Way - Get a Handle on It from the Consumer Financial Protection Bureau',
            contentTag: 'Article',
            category: 'Budgeting',
            thumbnail:
              '//images.ctfassets.net/y64yks2od8ab/JHp8dAdhyBFXVsBREmfo3/dd08cbfd3ee747506e241aef53617112/Debt_Getting_in_Your_Way_-_Get_a_Handle_on_It.jpg',
            slug: '/resources/blog/get-a-handle-on-debt/',
            excerpt:
              'Debt getting in the way of homeownership? Check out these 8 tools to help you get a handle on debt. ',
            mediaType: 'Image',
          },
          {
            title:
              'Building Your Savings - Start with Smart Goals from the Consumer Financial Protection Bureau',
            contentTag: 'Guide',
            category: 'Budgeting',
            thumbnail:
              '//images.ctfassets.net/y64yks2od8ab/5BLnpbGLxvC34UsynhvGKI/d25753e402859a7c589a17bf19f65f75/Building_Your_Savings_-_Start_with_Small_Goals.jpg',
            slug: '/resources/blog/building-your-savings/',
            excerpt:
              'Learn how to build up your savings with these smart goal tips ',
            mediaType: 'Image',
          },
        ]}
      />

      <CTACardv2
        preHeaderContent={'Have Questions?'}
        headerContent={`We're Here to Help`}
        buttonText={'Contact Us'}
        buttonUrl={'/contact-us/'}
        gtmClassName={'contact'}
        ctaIntro={
          <>
            <p>
              Existing customers <a href='tel:1-800-970-7250'>1-800-970-7250</a>
            </p>
            <p>
              New customers <a href='tel:1-866-701-0467'>1-866-701-0467</a>
            </p>
          </>
        }
      />
    </Layout>
  );
}

ForeclosurePrevention.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default ForeclosurePrevention;
